import React, { useMemo, useState, useEffect } from 'react'
import { Box, Button, Container, Grid } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { List } from 'immutable'
import Link from 'gui/components/link'
import { LINK_TARGET_BLANK } from 'gui/components/link/targets'
import {
  CONTENT_REFERENCE_FIELDS_MAIN,
  CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
  CONTENT_REFERENCE_TYPE_SERIES,
} from 'services/content-references/types'
import { CONTENT_TYPE_VIDEO } from 'services/content/content-types'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import ContentReferenceField from './content-references/ContentReferenceField'
import CopyMetadataButton from './content-references/CopyMetadataButton'
import { useContentReferencesSection } from './useContentReferencesSection'
import { COURSE_MODULE_REFERENCE_TYPES, COPY_METADATA_SOURCES } from './constants'
import type { SectionManagedSectionProps, ContentReferencesSectionContentData } from './types'

/** Content References main section: one constant per section. */
const VIDEO_CONTENT_REFERENCE_TYPES = (
  CONTENT_REFERENCE_FIELDS_MAIN.get(CONTENT_TYPE_VIDEO, List()) as List<string>
).toArray() as string[]

export type VideoContentReferencesSectionContentData = ContentReferencesSectionContentData

export interface VideoContentReferencesSectionProps extends SectionManagedSectionProps {
  contentData: ContentReferencesSectionContentData | null
}

/**
 * Video Content References Section (section-managed).
 * Uses useContentReferencesSection for state/mutations/save; renders fields via ContentReferenceField.
 *
 * Query dependency: This section expects contentData.contentReferencesFrom to include all types
 * it uses (VIDEO_CONTENT_REFERENCE_TYPES, plus primary-course when the video is part of a course).
 * The page's GraphQL query (GET_VIDEO_CONTENT in queries.ts) must request contentReferencesFrom(types:
 * [these types]). If the query omits them, existing refs won't load and saved refs will disappear after refetch.
 */
function VideoContentReferencesSection ({
  contentData,
  isViewOnly,
  onDirtyChange,
  saveHandlerRef,
  onResetTriggered,
}: VideoContentReferencesSectionProps) {
  const isPartOfCourse = useMemo(() => {
    const refs = contentData?.contentReferencesFrom ?? []
    return refs.some(
      (ref) =>
        ref?.contentReferenceType?.type != null &&
        COURSE_MODULE_REFERENCE_TYPES.includes(ref.contentReferenceType.type),
    )
  }, [contentData?.contentReferencesFrom])

  const refTypes = useMemo(
    () => [
      ...VIDEO_CONTENT_REFERENCE_TYPES,
      ...(isPartOfCourse ? [CONTENT_REFERENCE_TYPE_PRIMARY_COURSE] : []),
    ],
    [isPartOfCourse],
  )

  const { contentReferences, handleContentReferencesChange, bulkSetContentReferences } =
    useContentReferencesSection({
      refTypes,
      contentData,
      onDirtyChange,
      saveHandlerRef,
      onResetTriggered,
    })

  const [changedTriggers, setChangedTriggers] = useState<string[]>([])

  useEffect(() => {
    if (onResetTriggered !== undefined && onResetTriggered > 0) {
      setChangedTriggers([])
    }
  }, [onResetTriggered])

  return (
    <Container className="content-detail__section content-references" maxWidth={false}>
      <SectionAccordion title="Content References" collapsed={false}>
        {refTypes.map((referenceType) => {
          const sourceConfig = COPY_METADATA_SOURCES.find((c) => c.triggerRefType === referenceType)
          const value = contentReferences[referenceType] || []
          const hasTriggerValue = value.length > 0
          const selectedContentId = value[0]?.contentId
          const isTriggerChanged = changedTriggers.includes(referenceType)
          const showCopyButton =
            sourceConfig &&
            hasTriggerValue &&
            isTriggerChanged &&
            !isViewOnly &&
            selectedContentId != null

          const isLinkableType =
            referenceType === CONTENT_REFERENCE_TYPE_SERIES ||
            referenceType === CONTENT_REFERENCE_TYPE_PRIMARY_COURSE

          return (
            <React.Fragment key={referenceType}>
              <ContentReferenceField
                referenceType={referenceType}
                value={value}
                onChange={(newReferences) => {
                  handleContentReferencesChange(referenceType, newReferences)
                  if (sourceConfig) {
                    setChangedTriggers((prev) =>
                      prev.includes(referenceType) ? prev : [...prev, referenceType],
                    )
                  }
                }}
                disabled={isViewOnly}
                tooltip={
                  referenceType === CONTENT_REFERENCE_TYPE_PRIMARY_COURSE
                    ? 'This video is part of a course. By picking a "Primary Course" for this piece of content, the video will link to this course in specific scenarios on the Front End.'
                    : undefined
                }
              />
              {isLinkableType && value.length > 0 && (
                <Container maxWidth={false} disableGutters sx={{ mt: -1, mb: 1 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={2} />
                    <Grid item xs={10}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Link href={`/content/${value[0].id}`} target={LINK_TARGET_BLANK}>
                          <Button
                            variant="text"
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            sx={{ textTransform: 'none' }}
                          >
                            {value[0].title}
                          </Button>
                        </Link>
                        {showCopyButton && sourceConfig != null && (
                          <CopyMetadataButton
                            sourceConfig={sourceConfig}
                            selectedContentId={selectedContentId!}
                            onApply={bulkSetContentReferences}
                            onApplied={() =>
                              setChangedTriggers((prev) => prev.filter((t) => t !== referenceType))
                            }
                            disabled={isViewOnly}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Container>
              )}
            </React.Fragment>
          )
        })}
      </SectionAccordion>
    </Container>
  )
}

export default VideoContentReferencesSection
